import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ArrowLeft } from "lucide-react";
import { brand } from "@/config/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_TRIES = 6;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    let timer;
    let tries = 0;

    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") return setState("paid");
        if (["failed", "expired"].includes(data.payment_status)) return setState("error");
        if (tries >= MAX_TRIES) return setState("pending");
      } catch {
        if (cancelled) return;
        if (tries >= MAX_TRIES) return setState("error");
      }
      tries += 1;
      timer = setTimeout(tick, 2000);
    };

    tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="flex min-h-screen flex-col items-center justify-center bg-base px-6 text-center">
      <div className="w-full max-w-md rounded-[10px] border border-black/10 bg-white p-10 shadow-[0_30px_70px_-40px_rgba(0,0,0,0.4)]">
        <img src={brand.logo_url} alt={brand.brand_name} className="mx-auto mb-8 h-10 w-auto object-contain" />

        {state === "checking" && (
          <>
            <Loader2 className="mx-auto animate-spin text-black" size={40} />
            <h1 data-testid="payment-status-checking" className="mt-6 text-2xl font-black tracking-tight">Confirming your payment…</h1>
            <p className="mt-3 text-sm font-light text-black/55">This only takes a moment.</p>
          </>
        )}

        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-black" size={48} />
            <h1 data-testid="payment-status-paid" className="mt-6 text-3xl font-black tracking-tight">You're all set!</h1>
            <p className="mt-3 text-sm font-light leading-relaxed text-black/60">
              Your payment was successful. Welcome aboard — your growth engine is ready to start
              collecting reviews and building visibility.
            </p>
          </>
        )}

        {state === "pending" && (
          <>
            <Loader2 className="mx-auto animate-spin text-black" size={40} />
            <h1 data-testid="payment-status-pending" className="mt-6 text-2xl font-black tracking-tight">Almost there</h1>
            <p className="mt-3 text-sm font-light leading-relaxed text-black/60">
              Your payment is still processing. You'll receive a confirmation shortly — no need to pay again.
            </p>
          </>
        )}

        {state === "error" && (
          <>
            <XCircle className="mx-auto text-black" size={44} />
            <h1 data-testid="payment-status-error" className="mt-6 text-2xl font-black tracking-tight">We couldn't confirm this</h1>
            <p className="mt-3 text-sm font-light leading-relaxed text-black/60">
              If you completed payment, it may take a moment to reflect. Otherwise, please try again.
            </p>
          </>
        )}

        <Link
          data-testid="payment-back-home-link"
          to="/"
          className="mt-8 inline-flex items-center justify-center gap-2 rounded-[10px] bg-black px-6 py-3.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
        >
          <ArrowLeft size={15} /> Back to home
        </Link>
      </div>
    </div>
  );
}
